import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

const fmtTime = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const fmtDate = () => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const STEP_PASSWORD = 'password';
const STEP_FORM     = 'form';
const STEP_SUCCESS  = 'success';

// ── Device detection ──────────────────────────────────────────────────────────
// The QR code is intended for desktop kiosks only. On phones and tablets we
// hide it — the visitor is already on a handheld, so offering another QR to
// scan with their phone makes no sense.
function isHandheldDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Phones + Android tablets + anything that explicitly identifies as mobile
  if (/Mobi|Android|iPhone|iPod|Tablet|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return true;
  // Modern iPadOS reports as Macintosh but has touch — use maxTouchPoints to disambiguate
  if (navigator.maxTouchPoints > 1 && /Macintosh/i.test(ua)) return true;
  return false;
}

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return w;
}

export default function ProspectKioskPage() {
  const pathParts = window.location.pathname.split('/');
  const token = pathParts[pathParts.length - 1];
  const width = useWindowWidth();

  // Breakpoints (width-based, used for sizing)
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;
  const isDesktop = width >= 1024;

  // Device-based (used for deciding whether to show the QR at all)
  const isHandheld = useMemo(() => isHandheldDevice(), []);
  const showQr = !isHandheld;

  const [formDef, setFormDef]         = useState(null);
  const [step, setStep]               = useState(null);
  const [time, setTime]               = useState(fmtTime());
  const [loadError, setLoadError]     = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const [password, setPassword]       = useState('');
  const [pwError, setPwError]         = useState('');
  const [pwChecking, setPwChecking]   = useState(false);
  const [pwUnlocked, setPwUnlocked]   = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail]             = useState('');
  const [phone, setPhone]             = useState('');
  const [customData, setCustomData]   = useState({});

  const resetTimerRef = useRef(null);

  // Override global overflow:hidden
  useEffect(() => {
    const root = document.getElementById('root');
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (root) { root.style.overflow = ''; root.style.height = ''; }
    };
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setTime(fmtTime()), 15000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (!token) { setLoadError('No form token in URL.'); return; }
    api.get(`/kiosk-forms/public/form/${token}`)
      .then(r => {
        setFormDef(r.data);
        setStep(r.data.password_protected ? STEP_PASSWORD : STEP_FORM);
        if (!r.data.password_protected) setPwUnlocked(true);
      })
      .catch(() => setLoadError('Form not found. Please check the URL or QR code.'));
  }, [token]);

  const scheduleReset = useCallback(() => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(resetAll, 90000);
  }, []); // eslint-disable-line

  const resetAll = () => {
    setStep(formDef?.password_protected && !pwUnlocked ? STEP_PASSWORD : STEP_FORM);
    setCompanyName(''); setContactName(''); setEmail(''); setPhone(''); setCustomData({});
    setSubmitError(null);
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
  };

  const handleActivity = () => scheduleReset();

  const checkPassword = async () => {
    setPwChecking(true); setPwError('');
    try {
      const { data } = await api.post(`/kiosk-forms/public/form/${token}/verify-password`, { password });
      if (data.ok) { setPwUnlocked(true); setStep(STEP_FORM); }
      else setPwError('Incorrect password. Please try again.');
    } catch(e) { setPwError('Unable to check password. Please try again.'); }
    setPwChecking(false);
  };

  const handleSubmit = async () => {
    if (!companyName.trim()) return;
    setSubmitting(true); setSubmitError(null);
    try {
      await api.post(`/kiosk-forms/public/form/${token}/submit`, {
        company_name: companyName.trim(),
        contact_name: contactName.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        custom_data: customData,
      });
      setStep(STEP_SUCCESS);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setStep(STEP_FORM);
        setCompanyName(''); setContactName(''); setEmail(''); setPhone(''); setCustomData({});
      }, 6000);
    } catch(e) {
      setSubmitError(e.response?.data?.error || 'Submission failed. Please try again.');
    }
    setSubmitting(false);
  };

  const brandColor = formDef?.branding_color || '#EE7203';
  const qrUrl = `${window.location.origin}/api/kiosk-forms/public/qr/${token}`;

  // ── Responsive style values ───────────────────────────────────────────────
  const pad     = isMobile ? '16px' : isTablet ? '24px 28px' : '28px 40px';
  const titleSz = isMobile ? 26 : isTablet ? 32 : 38;
  const descSz  = isMobile ? 14 : 16;
  const qrSize  = isMobile ? 180 : isTablet ? 220 : 280;
  const inputSz = isMobile ? 16 : 17;
  const cardPad = isMobile ? 20 : isTablet ? 28 : 36;

  if (loadError) return (
    <div style={S.page} onClick={handleActivity}>
      <div style={S.center}>
        <div style={{ ...S.card, maxWidth: 420, padding: cardPad }}>
          <div style={{ fontSize: 48, textAlign: 'center' }}>⚠️</div>
          <p style={{ textAlign: 'center', fontSize: 18, color: '#718096', marginTop: 16 }}>{loadError}</p>
        </div>
      </div>
    </div>
  );

  if (!formDef) return (
    <div style={S.page}>
      <div style={S.center}><div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 20 }}>Loading…</div></div>
    </div>
  );

  return (
    <div style={S.page} onClick={handleActivity} onKeyDown={handleActivity}>

      {/* ── Top bar ── */}
      <div style={{ ...S.topBar, padding: isMobile ? '12px 16px' : '14px 28px' }}>
        <span style={{ ...S.timeText, fontSize: isMobile ? 20 : 26 }}>{time}</span>
        <span style={{ ...S.dateText, fontSize: isMobile ? 11 : 14 }}>{fmtDate()}</span>
      </div>

      <div style={S.bodyWrap}>

        {/* ── PASSWORD ── */}
        {step === STEP_PASSWORD && (
          <div style={S.center}>
            <div style={{ ...S.card, maxWidth: 420, padding: cardPad, width: isMobile ? 'calc(100% - 32px)' : undefined }}>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{ fontSize: 48 }}>🔒</div>
                <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: '#1a202c', margin: '12px 0 4px' }}>{formDef.title}</h2>
                <p style={{ fontSize: 15, color: '#718096' }}>This form is password protected.</p>
              </div>
              <label style={S.label}>Password</label>
              <input style={{ ...S.input, fontSize: inputSz }} type="password" value={password}
                onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && checkPassword()}
                autoFocus placeholder="Enter password…" />
              {pwError && <p style={{ color: '#e53e3e', fontSize: 14, marginTop: 8 }}>{pwError}</p>}
              <button style={{ ...S.btnPrimary, background: brandColor, marginTop: 16, fontSize: isMobile ? 17 : 20, opacity: pwChecking ? 0.6 : 1 }}
                onClick={checkPassword} disabled={pwChecking || !password}>
                {pwChecking ? 'Checking…' : 'Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* ── FORM ── */}
        {step === STEP_FORM && (
          <div style={{ padding: pad, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 20 : 28 }}>

            {/* Header */}
            <div style={{ textAlign: 'center', width: '100%', maxWidth: 1100 }}>
              <h1 style={{ fontSize: titleSz, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-1px',
                margin: '0 0 10px', color: brandColor }}>
                {formDef.title}
              </h1>
              {formDef.description && (
                <p style={{ fontSize: descSz, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, margin: 0 }}>
                  {formDef.description}
                </p>
              )}
            </div>

            {/* Form + QR row — on desktop QR sits beside the form; on any handheld device the QR is hidden */}
            <div style={{
              display: 'flex',
              flexDirection: (isMobile || !showQr) ? 'column' : 'row',
              gap: isMobile ? 20 : 28,
              alignItems: (isMobile || !showQr) ? 'center' : 'flex-start',
              width: '100%',
              maxWidth: showQr ? 1100 : 640,
            }}>
              {/* Form card */}
              <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                <div style={{ ...S.card, padding: cardPad }}>
                  <h2 style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, color: '#1a202c', marginBottom: 20 }}>
                    Tell us about yourself
                  </h2>

                  <FieldRow label="Company Name *">
                    <input style={{ ...S.input, fontSize: inputSz }} value={companyName}
                      onChange={e => setCompanyName(e.target.value)} placeholder="Your company…" autoComplete="organization" />
                  </FieldRow>

                  <FieldRow label="Your Name">
                    <input style={{ ...S.input, fontSize: inputSz }} value={contactName}
                      onChange={e => setContactName(e.target.value)} placeholder="First and last name…" autoComplete="name" />
                  </FieldRow>

                  {formDef.show_email && (
                    <FieldRow label="Email Address">
                      <input style={{ ...S.input, fontSize: inputSz }} type="email" value={email}
                        onChange={e => setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email" />
                    </FieldRow>
                  )}

                  {formDef.show_phone && (
                    <FieldRow label="Phone Number">
                      <input style={{ ...S.input, fontSize: inputSz }} type="tel" value={phone}
                        onChange={e => setPhone(e.target.value)} placeholder="+44 …" autoComplete="tel" />
                    </FieldRow>
                  )}

                  {(formDef.custom_fields || []).map(field => (
                    <FieldRow key={field.id} label={field.label + (field.required ? ' *' : '')}>
                      {field.type === 'textarea' ? (
                        <textarea style={{ ...S.input, fontSize: inputSz, minHeight: isMobile ? 70 : 90, resize: 'vertical', fontFamily: 'inherit' }}
                          value={customData[field.id] || ''} onChange={e => setCustomData(d => ({ ...d, [field.id]: e.target.value }))}
                          placeholder={field.placeholder || ''} />
                      ) : field.type === 'select' ? (
                        <select style={{ ...S.input, fontSize: inputSz }} value={customData[field.id] || ''}
                          onChange={e => setCustomData(d => ({ ...d, [field.id]: e.target.value }))}>
                          <option value="">Select an option…</option>
                          {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                      ) : field.type === 'radio' ? (
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: isMobile ? 8 : 12, marginTop: 4 }}>
                          {(field.options || []).map(opt => (
                            <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: inputSz }}>
                              <input type="radio" name={field.id} value={opt}
                                checked={customData[field.id] === opt}
                                onChange={() => setCustomData(d => ({ ...d, [field.id]: opt }))}
                                style={{ width: 18, height: 18, accentColor: brandColor }} />
                              {opt}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <input style={{ ...S.input, fontSize: inputSz }}
                          type={field.type === 'number' ? 'number' : 'text'}
                          value={customData[field.id] || ''}
                          onChange={e => setCustomData(d => ({ ...d, [field.id]: e.target.value }))}
                          placeholder={field.placeholder || ''} />
                      )}
                    </FieldRow>
                  ))}

                  {submitError && (
                    <div style={{ background: '#fff5f5', border: '1px solid #fed7d7', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#c53030', fontSize: 14 }}>
                      {submitError}
                    </div>
                  )}

                  <button style={{ ...S.btnPrimary, background: brandColor, marginTop: 8,
                    fontSize: isMobile ? 17 : 20, padding: isMobile ? '14px 20px' : '16px 24px',
                    opacity: (submitting || !companyName.trim()) ? 0.5 : 1 }}
                    onClick={handleSubmit} disabled={submitting || !companyName.trim()}>
                    {submitting ? 'Submitting…' : '✓ Submit'}
                  </button>
                </div>
              </div>

              {/* QR beside form — desktop only (hidden on phones/tablets) */}
              {showQr && !isMobile && formDef.qr_enabled && (
                <QrBlock qrUrl={qrUrl} qrSize={qrSize} isTablet={isTablet} isDesktop={isDesktop} />
              )}
            </div>
          </div>
        )}

        {/* ── SUCCESS ── */}
        {step === STEP_SUCCESS && (
          <div style={S.center}>
            <div style={{ ...S.card, maxWidth: 480, padding: cardPad, textAlign: 'center',
              width: isMobile ? 'calc(100% - 32px)' : undefined }}>
              <div style={{ fontSize: isMobile ? 48 : 64, marginBottom: 12 }}>✅</div>
              <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 800, color: '#1a202c', marginBottom: 8 }}>Thank you!</h1>
              <p style={{ fontSize: isMobile ? 15 : 18, color: '#4a5568', marginBottom: 8 }}>
                We've received your details and we'll be in touch soon.
              </p>
              <p style={{ fontSize: 14, color: '#a0aec0', marginTop: 24 }}>This screen will reset in a moment…</p>
              <button style={{ ...S.btnPrimary, background: brandColor, marginTop: 24, fontSize: isMobile ? 16 : 18 }}
                onClick={() => {
                  if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
                  setStep(STEP_FORM);
                  setCompanyName(''); setContactName(''); setEmail(''); setPhone(''); setCustomData({});
                }}>
                New Entry
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── QR block component ────────────────────────────────────────────────────────
function QrBlock({ qrUrl, qrSize, isMobile, isTablet }) {
  const panelWidth = isMobile ? '100%' : isTablet ? 260 : 310;
  return (
    <div style={{
      width: panelWidth,
      flexShrink: 0,
      display: 'flex',
      flexDirection: isMobile ? 'row' : 'column',
      alignItems: 'center',
      gap: isMobile ? 16 : 0,
      background: 'rgba(255,255,255,0.06)',
      borderRadius: 20,
      padding: isMobile ? '16px 20px' : '28px 20px 20px',
      position: isMobile ? 'static' : 'sticky',
      top: isMobile ? undefined : 76,
    }}>
      <img src={qrUrl} alt="QR Code" style={{
        width: qrSize, height: qrSize, flexShrink: 0,
        borderRadius: 10, background: '#fff', padding: 6,
        order: isMobile ? 1 : 0,
      }} />
      <div style={{ textAlign: isMobile ? 'left' : 'center', order: isMobile ? 0 : 1 }}>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: isMobile ? 15 : 17,
          fontWeight: 700, lineHeight: 1.4, margin: isMobile ? 0 : '18px 0 0' }}>
          {isMobile ? 'Prefer to use your phone? Scan the QR code →' : <>Prefer to fill this in<br />on your phone?</>}
        </p>
        {!isMobile && <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 10 }}>Scan to open on your device</p>}
      </div>
    </div>
  );
}

// ── Field row ─────────────────────────────────────────────────────────────────
function FieldRow({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

// ── Static styles ─────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 60%, #1a202c 100%)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Segoe UI', Arial, sans-serif",
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(0,0,0,0.3)',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  timeText: { fontWeight: 800, color: '#fff', letterSpacing: 2 },
  dateText: { color: 'rgba(255,255,255,0.55)' },
  bodyWrap: { flex: 1, display: 'flex', flexDirection: 'column' },
  center: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    background: '#fff',
    borderRadius: 20,
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
    width: '100%',
  },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: '#4a5568',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    marginBottom: 7,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e2e8f0',
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  },
  btnPrimary: {
    display: 'block',
    width: '100%',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    fontWeight: 800,
    cursor: 'pointer',
    letterSpacing: 0.3,
  },
};
